import {
  consoleExport,
  createClientApmExporterWithTransport,
  createClientErrorTrackingExporterWithTransport,
  createHttpTransport,
  createRootTel,
  type RootTel,
  type Tel,
} from '@w/tel'
import { _initBrowserClientTel } from '@w/tel/browser-client'
import { createIdbExporter, recordRemoteTraces } from '@w/tel-ui'
import { feEnvVars } from './fe-env-vars'
import { isSensitive } from './telemetry'
import { boundTransport } from './telemetry-transport'
import { errorNotifications } from './shell/error-notifications'

export interface FlashcardsClientTel {
  root: RootTel
  readonly current: Tel
  /** Binds the `/tel` export to the composition root's principal fetch (telemetry-transport.ts). */
  bindTransport(this: void, fetch: Window['fetch']): void
  /** The `/tel` export's transport: waits for, then calls, the bound fetch. */
  readonly transport: Window['fetch']
}

export function createFlashcardsClientTel({
  env,
  httpUrl = `${feEnvVars.BACKEND_URL}/tel`,
  console: printToConsole = true,
}: {
  env: string
  httpUrl?: string | null
  /** false in tests: the console must stay clean (scripts/console-guard.ts); IndexedDB still records. */
  console?: boolean
}): FlashcardsClientTel {
  const handlers = printToConsole ? [consoleExport({
    labels: 'all',
    targets: 'all',
    minimal: false,
    spans: false,
    metrics: { delayMs: 60_000 },
  })] : []
  const transport = boundTransport()
  if (typeof window !== 'undefined') {
    const client = _initBrowserClientTel({
      name: 'flashcards-client',
      env,
      handlers: [],
      isSensitive,
    })
    client.root.setHandlers(...handlers, errorNotifications(), createIdbExporter(), ...(httpUrl ? createBrowserHttpExporters(httpUrl, client.root, transport.fetch) : []))
    // Assign, not spread: `current` is a getter that follows route traces (browser-client.ts).
    return Object.assign(client, { bindTransport: transport.bind, transport: transport.fetch })
  }

  const root = createRootTel({
    name: 'flashcards-client',
    origin: 'client',
    env,
    isSensitive,
  })
  root.setHandlers(
    ...handlers,
    errorNotifications(),
    ...(typeof indexedDB === 'undefined' ? [] : [createIdbExporter()]),
  )
  return { root, current: root, bindTransport: transport.bind, transport: transport.fetch }
}

/** `send` is the app's principal fetch, which omits these credentials while the principal is anonymous. */
export function createBrowserHttpExporters(url: string, tel: Tel, send: Window['fetch']) {
  const browserFetch = fetch
  const fetchWithCredentials = Object.assign(
    (input: Parameters<typeof fetch>[0], init?: Parameters<typeof fetch>[1]) =>
      send(input, { ...init, credentials: 'include' }),
    {
      preconnect(
        url: string | URL,
        options?: { dns?: boolean; tcp?: boolean; http?: boolean; https?: boolean },
      ) {
        if ('preconnect' in browserFetch && typeof browserFetch.preconnect === 'function') {
          browserFetch.preconnect(url, options)
        }
      },
    },
  )
  const httpTransport = createHttpTransport({ url, fetch: fetchWithCredentials })
  const transport = recordRemoteTraces(httpTransport, {
    tel,
    dbName: 'tel',
    url: new URL(url, location.href).toString(),
  })
  return [
    createClientErrorTrackingExporterWithTransport({ transport }),
    createClientApmExporterWithTransport({ transport }),
  ]
}
